import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import FormCheckBox from 'common/components/form/components/form-check-box';
import FormFieldLabel from 'common/components/form/components/form-field-label';
import FormFieldErrors from 'common/components/form/components/form-field-errors';
import FormSelect from 'common/components/form/components/form-select';
import FormSelectMultiple from 'common/components/form/components/form-select-multiple';
import FormOrderedSelectMultiple from 'common/components/form/components/form-ordered-select-multiple';
import FormTabBar from 'common/components/form/components/form-tab-bar';
import FormTextArea from 'common/components/form/components/form-text-area';
import {
  FusionCheckboxAdapter,
  FusionDatePickerAdapter,
  FusionInputAdapter,
  FusionSelectAdapter,
  FusionSelectMultipleAdapter,
  FusionSelectMultipleFilterAdapter,
  FusionTextAreaAdapter,
} from '../fusion-adapters';
import {FormThemeEnum} from '../../utils';

import './index.scss';

const LEGACY_FORM_MAP = {
  checkbox: FormCheckBox,
  orderedselectmultiple: FormOrderedSelectMultiple,
  tabbar: FormTabBar,
  select: FormSelect,
  selectmultiple: FormSelectMultiple,
  textarea: FormTextArea,
};

const FUSION_FORM_MAP = {
  checkbox: FusionCheckboxAdapter,
  datetime: FusionDatePickerAdapter,
  orderedselectmultiple: FormOrderedSelectMultiple, // @todo: make Fusion versions of these, if needed
  tabbar: FormTabBar, // @todo: make Fusion versions of these, if needed
  select: FusionSelectAdapter,
  selectmultiple: FusionSelectMultipleAdapter,
  selectmultiplefilter: FusionSelectMultipleFilterAdapter,
  text: FusionInputAdapter,
  textarea: FusionTextAreaAdapter,
};

export default class FormField extends React.Component {
  static propTypes = {
    data: PropTypes.any,
    disabled: PropTypes.bool,
    errors: PropTypes.any,
    field: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    theme: PropTypes.oneOf(Object.values(FormThemeEnum)),
  };

  static defaultProps = {
    theme: FormThemeEnum.LEGACY,
  };

  getErrors() {
    const {errors} = this.props;
    return errors && <FormFieldErrors errors={errors} />;
  }

  getLabel() {
    const {theme} = this.props;
    if (theme !== FormThemeEnum.LEGACY) return null;

    const {label, help_text} = this.props.field;
    return <FormFieldLabel label={label} tooltip={help_text} />;
  }

  getField() {
    const {field, theme} = this.props;
    const inputType = field.widget.input_type;
    const customDataStructure = field.custom_data_structure;

    // A hidden input has no need render into the DOM.
    // It's value is stored in redux, but it does not show in the UI.
    if (inputType === 'hidden') {
      return null;
    }

    const themeMap =
      theme === FormThemeEnum.FUSION ? FUSION_FORM_MAP : LEGACY_FORM_MAP;
    const component = themeMap[inputType];

    if (!component) {
      return `No component found for input type: ${inputType}`;
    }

    const props = {...this.props};
    if (customDataStructure && typeof props.data === 'string') {
      // if JSON is POSTed to the server, it may be returned
      // as a primitive or as JSON in certain cases, such as
      // when there are form errors
      props.data = JSON.parse(props.data);
    }
    return React.createElement(component, props);
  }

  render() {
    const hidden = this.props.field.widget.is_hidden;
    const isFusion = this.props.theme === FormThemeEnum.FUSION;

    const field = this.getField();
    const label = this.getLabel();
    const errors = this.getErrors();
    const className = classNames('FormField', {
      'FormField--hidden': hidden,
      'FormField--errors': errors && !isFusion,
    });
    return (
      <div className={className} id={`${this.props.name}Field`}>
        <span className={'FormField-label'}>{label}</span>
        <span className={'FormField-field'}>{field}</span>
        {!isFusion ? (
          <span className={'FormField-errors'}>{errors}</span>
        ) : null}
      </div>
    );
  }
}
